import AppConfig from '@/app-config'
import React, { useMemo, useRef, useState, useEffect } from 'react'
import useStore from '@/store'
import { CSSTransition, TransitionGroup } from 'react-transition-group'
import classNames from 'classnames'
import type { Props } from './types'
import type { Resident } from '@/store/types'
import LocationModalContent from '../location-modal-content'
import styles from './style.module.scss'
import Flag from '@/components/flag'
import WrappedText from '@/components/wrapped-text'


const ResidentsModalContent: React.FC<Props> = ({
  building,
  residents,
  type,
  level = 0,
  side,
}) => {
  const scrollerRef = useRef<HTMLDivElement | null>(null)
  const [activeLevel, setActiveLevel] = useState(level)
  const [scrollUpHidden, setScrollUpHidden] = useState(false)
  const [scrollDownHidden, setScrollDownHidden] = useState(false)
  const modals = useStore((state) => state.modals)
  const setModal = useStore((state) => state.setModal)
  const goLastModal = useStore((state) => state.goLastModal)
  const saveModalState = useStore((state) => state.saveModalState)
  const activeCategoryFilter = useStore((state) => state.activeCategoryFilter)
  const sounds = useStore((state) => state.sounds)
  const levelData = useMemo(() => {
    if (!building)
      return [];
    return building?.attributes.Levels.map((level, index) => ({
      label: level.Label,
      levelNum: index,
      defaultLevel: level.Default,
      residents: residents?.filter(
        (r) => r.attributes.Level === level.Reference),
      image: `${AppConfig.apiUrl}${level.Image.data.attributes.url}`
    }))
  }, [building, residents])

  const classes = classNames(styles['residents-modal-content'], [
    styles[`type--${type}`],
  ])

  useEffect(() => {
    let defaultLevel = levelData.find((level) => level.defaultLevel)?.levelNum
    if (modals[side]?.state && modals[side]?.state.activeLevel !== undefined)
        defaultLevel = modals[side]?.state.activeLevel
    if (defaultLevel !== undefined)
      setActiveLevel(defaultLevel)

    // eslint-disable-next-line
  }, [])

  useEffect(onScroll, [activeLevel])

  function onResidentClick(r: Resident) {
    sounds?.click1?.play()
    saveModalState({ activeLevel }, side)
    setModal(
      {
        content: (
          <LocationModalContent
            title={r.attributes.Title}
            text={r.attributes.Data.Text}
            image={r.attributes.Data.Image?.Image}
            caption={r.attributes.Data.Image?.Caption}
          />
        ),
        type: 'resident',
        closeAction: () => goLastModal(side),
      },
      side
    )
  }

  function scrollResidents(direction: number) {
    if (!scrollerRef.current)
      return

    scrollerRef.current.scrollBy({
      top: direction * scrollerRef.current.clientHeight * 0.6,
      behavior: 'smooth'
    })
  }

  function onScroll() {
    if (!scrollerRef.current)
      return

    setScrollUpHidden(scrollerRef.current.scrollTop === 0)
    setScrollDownHidden(scrollerRef.current.scrollHeight
      - scrollerRef.current.scrollTop - scrollerRef.current.clientHeight === 0)
  }

  const filteredResidents = useMemo(() => {
    return levelData[activeLevel]?.residents;
  }, [levelData, activeLevel])

  const transitionGroup = useMemo(() => {
    return (
      <TransitionGroup component={null}>
        {levelData.map((level) => {
          if (level.levelNum > activeLevel)
            return null

          const figNodeRef = React.createRef<HTMLElement>()
          return (
            <CSSTransition
              key={level.levelNum}
              timeout={{
                enter: 0,
                exit: 255,
              }}
              nodeRef={figNodeRef}
              classNames={{
                enter: styles['axonometry--enter'],
                enterActive: styles['axonometry--enter-active'],
                enterDone: styles['axonometry--enter-done'],
                exitActive: styles['axonometry--exit-active'],
                exitDone: styles['axonometry--exit-done'],
              }}
            >
              <figure
                className={styles.axonometry}
                ref={figNodeRef}
              >
                <img src={level.image} alt="" />
                {level.levelNum === activeLevel && level.residents?.map((r) => {
                    return r?.attributes.Flag ? (
                      <Flag
                        key={r?.id}
                        text={r?.attributes.Label}
                        placement={r?.attributes.Flag}
                        main={r?.attributes.Main}
                        type={type}
                        category={r.attributes.Category.data}
                        highlighted={
                          activeCategoryFilter[side] ===
                          r.attributes.Category.data.attributes.Slug
                        }
                        clickHandler={() => onResidentClick(r)}
                        extraClasses={styles['axonometry-flag']}
                      />
                    ) : null
                  })
                }
              </figure>
            </CSSTransition>
          )
        })}
      </TransitionGroup>
    )
    // eslint-disable-next-line
  }, [activeLevel, activeCategoryFilter])

  return (
    <div className={classes}>
      <div className={styles.axonometries}>
        {transitionGroup}
        {building?.attributes.Label ? (
            <WrappedText className={styles.title}
              text={building.attributes.Label} />
          ) : null
        }
      </div>
      {filteredResidents?.length ? (
        <div className={styles['residents-scroller']}>
          <div className={styles['residents-scroller-title']}>
            <span />
            <span>Current Residents</span>
          </div>
          <button
            className={classNames(styles['scroll-button'], {
              [styles.hidden]: scrollUpHidden
            })}
            onClick={() => scrollResidents(-1)}>
            <img
              src={require('@/assets/images/scroll-arrow.svg').default}
              alt="up"
              />
          </button>
          <div ref={scrollerRef} className={styles['residents-scroller-container']}
            onScroll={() => onScroll()}>
            {filteredResidents
              ?.sort((a, b) =>
                a.attributes.Title > b.attributes.Title ? 1 : -1
              )
              .map((r) => (
                <div
                  key={r.id}
                  className={styles['residents-scroller-resident']}
                  style={{
                    color: Object.values(activeCategoryFilter).includes(
                      r.attributes.Category.data.attributes.Slug
                    )
                      ? r.attributes.Category.data.attributes.Color
                      : undefined,
                  }}
                  onClick={() => onResidentClick(r)}
                >
                  <span
                    className={styles['residents-scroller-resident-number']}
                  >
                    •
                  </span>
                  <span className={styles['residents-scroller-resident-name']}>
                    {r.attributes.Title}
                  </span>
                </div>
              ))}
          </div>
          <button
            className={classNames(styles['scroll-button'], {
              [styles.hidden]: scrollDownHidden
            })}
            onClick={() => scrollResidents(1)}>
            <img className={styles['scroll-down']}
              src={require('@/assets/images/scroll-arrow.svg').default}
              alt="down"
              />
          </button>
        </div>
      ) : null}
      {levelData.length > 1 ? (
        <ul className={styles['level-switcher']}>
          {levelData.map((level) => {
              return (
                <li
                  key={level.levelNum}
                  className={classNames(styles['level-switcher-switch'], {
                    [styles.active]: activeLevel === level.levelNum,
                  })}
                  onClick={() => setActiveLevel(level.levelNum)}
                >
                  {level.label}
                </li>
              )
            }).reverse()
          }
        </ul>) : null
      }
    </div>
  )
}

export default React.memo(ResidentsModalContent)
